import { Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import InputLabel from "@material-ui/core/InputLabel";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { makeStyles } from "@material-ui/core/styles";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { changeLoginKey, changeMembercode } from "../Action/memberAction";
import { hexToBytes, saltedHashUtf16LE, toHex } from "../utils/hash";
import api from "../utils/api";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundImage:
      "linear-gradient(to right, var(--primaryGreen, #005c49), rgb(10, 36, 26))",
    width: "100%",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    width: "75%",
    minHeight: "80vh",
    backgroundColor: "rgb(10, 36, 26)",
    borderRadius: 16,
    boxShadow: "4px 2px 12px 8px var(--shadow)",
    display: "grid",
    gridTemplateColumns: "40% 60%",
    "@media screen and (max-width: 820px)": {
      gridTemplateColumns: "1fr",
      width: "inherit",
      margin: "0 20px",
    },
  },
  img: {
    margin: "auto",
    display: "block",
    maxWidth: "70%",
    maxHeight: "100%",
  },
  leftSide: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    backgroundColor: "#E7EAED",
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    "@media screen and (max-width: 820px)": {
      borderRadius: "16px",
    },
  },
  logo: {
    height: 115,
    "& > img": {
      height: 115,
    },
  },
  form: {
    margin: "32px 10px 0px 50px",
    width: "70%",
    "@media screen and (max-width: 820px)": {
      margin: "0",
      padding: "40px 20px",
      width: "inherit",
    },
  },
  formGroup: {
    width: "100%",
  },
  textfield: {
    marginBottom: 16,
  },
  btn: {
    borderRadius: "100px",
    marginTop: 25,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    cursor: "pointer",
  },
  rightSide: {
    backgroundImage:
      "linear-gradient(to right, rgba(70, 222, 164, 0.4), rgb(10, 36, 26))",
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "64px 148px",
    "@media screen and (max-width: 820px)": {
      display: "none",
    },
  },
  err: {
    color: "red",
  },
}));

const Login = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const [values, setValues] = useState({
    error: false,
    errMsg: "",
    password: "",
    showPassword: false,
    successMsg: "",
  });

  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleChange = (prop) => (event) => {
    setValues((prev) => ({
      ...prev,
      [prop]: event.target.value,
      error: prop === "password" ? false : prev.error,
      successMsg: "",
    }));
  };

  const handleClickShowPassword = () => {
    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handlechangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const handleLogin = async () => {
    setIsLoggingIn(true);
    try {
      const APIBASE_URL = process.env.REACT_APP_APIBASE_URL;
      const saltResp = await api.get(
        `${APIBASE_URL}/greenUserSalt?Username=${encodeURIComponent(email)}`
      );

      const saltHex =
        saltResp?.data?.SaltHex ||
        saltResp?.data?.[0]?.SaltHex ||
        saltResp?.data?.saltHex ||
        "";

      if (!saltHex) {
        // Treat missing salt as authentication failure (no user / mismatch)
        setValues((prev) => ({
          ...prev,
          error: true,
          errMsg: "User id-Password Mismatch!",
        }));
        return;
      }

      const saltBytes = hexToBytes(saltHex);
      const hashBytes = await saltedHashUtf16LE(values.password, saltBytes);
      const hashHex = toHex(hashBytes);

      const bodyParam = {
        Email: email,
        pwd: hashHex.toUpperCase(),
      };

      const res = await api.post(`${APIBASE_URL}/GreenUser`, bodyParam);

      if (res && res.data && res.data.length > 0) {
        const loggedInUser = res.data[0];
        const loggedInCompany =
          loggedInUser?.Company ||
          loggedInUser?.CompanyName ||
          loggedInUser?.MemberName ||
          "";

        localStorage.setItem("MemberDetails", JSON.stringify(res.data));
        localStorage.setItem("uId", loggedInUser?.UniqueKey);
        localStorage.setItem("MemberCode", loggedInUser?.MemberCode);
        localStorage.setItem("usr_role", loggedInUser?.UserRole);
        localStorage.setItem("Name", loggedInCompany);
        localStorage.setItem(
          "UserName",
          `${loggedInUser?.FName || ""} ${loggedInUser?.LName || ""}`.trim()
        );
        localStorage.setItem("LKey", loggedInUser?.LoginKey);
        localStorage.setItem("Lkey", loggedInUser?.LoginKey);
        dispatch(changeMembercode(loggedInUser?.MemberCode));
        dispatch(changeLoginKey(loggedInUser?.LoginKey));
        navigate("/MemberDetails");
        return;
      }

      // No matching user returned
      setValues((prev) => ({
        ...prev,
        error: true,
        errMsg: "User id-Password Mismatch!",
      }));
    } catch (e) {
      console.error("Login error:", e);
      setValues((prev) => ({
        ...prev,
        error: true,
        errMsg: "User id-Password Mismatch!",
      }));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleForgotPassword = () => {
    if (!email) {
      setValues((prev) => ({
        ...prev,
        error: true,
        errMsg: "Please enter your registered email.",
      }));
      return;
    }

    const APIBASE_URL = process.env.REACT_APP_APIBASE_URL;
    api
      .get(`/forgotPassword?email=${email}`)
      .then((res) => {
        // You can check res.data if your API returns a success flag or message
        setValues((prev) => ({
          ...prev,
          error: false,
          errMsg: "",
          successMsg: "Password sent to your email.",
        }));
        setIsForgotPassword(false); // Go back to login screen
      })
      .catch(() => {
        setValues((prev) => ({
          ...prev,
          error: true,
          errMsg: "Unable to send password. Please try again later.",
        }));
      });
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.leftSide}>
          <div className={classes.logo}>
            <img
              src={require("../Greenabl Logo_stacked_full color.png")}
              alt="logo"
            />
          </div>

          <div className={classes.form}>
            {!isForgotPassword ? (
              <>
                <FormControl
                  variant="outlined"
                  classes={{ root: classes.formGroup }}
                >
                  <InputLabel htmlFor="email">Email</InputLabel>
                  <OutlinedInput
                    id="email"
                    value={email}
                    fullWidth
                    onChange={handlechangeEmail}
                    label="Email"
                    className={classes.textfield}
                  />
                </FormControl>

                <FormControl
                  variant="outlined"
                  classes={{ root: classes.formGroup }}
                >
                  <InputLabel htmlFor="outlined-adornment-password">
                    Password
                  </InputLabel>
                  <OutlinedInput
                    id="outlined-adornment-password"
                    type={values.showPassword ? "text" : "password"}
                    value={values.password}
                    className={classes.textfield}
                    fullWidth
                    onChange={handleChange("password")}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {values.showPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {values.error && (
                    <FormHelperText className={classes.err}>
                      {values.errMsg}
                    </FormHelperText>
                  )}
                  {!values.error && values.successMsg && (
                    <FormHelperText style={{ color: "green" }}>
                      {values.successMsg}
                    </FormHelperText>
                  )}
                </FormControl>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  className={classes.btn}
                  onClick={handleLogin}
                  disabled={isLoggingIn}
                >
                  {isLoggingIn ? (
                    <>
                      <CircularProgress
                        size={18}
                        color="inherit"
                        style={{ marginRight: 8 }}
                      />
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>

                <div style={{ marginTop: 32 }}>
                  Forgot password?{" "}
                  <span
                    className={classes.link}
                    onClick={() => setIsForgotPassword(true)}
                  >
                    Click here
                  </span>
                </div>
              </>
            ) : (
              <>
                <Typography variant="h5" gutterBottom>
                  Forgot Password
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Enter your registered email address to reset your password.
                </Typography>

                <FormControl
                  variant="outlined"
                  classes={{ root: classes.formGroup }}
                >
                  <InputLabel htmlFor="forgot-email">Email</InputLabel>
                  <OutlinedInput
                    id="forgot-email"
                    value={email}
                    fullWidth
                    onChange={handlechangeEmail}
                    label="Email"
                    className={classes.textfield}
                  />
                  {values.error && (
                    <FormHelperText className={classes.err}>
                      {values.errMsg}
                    </FormHelperText>
                  )}
                </FormControl>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  className={classes.btn}
                  onClick={handleForgotPassword}
                >
                  Submit
                </Button>

                <div style={{ marginTop: 24 }}>
                  <span
                    className={classes.link}
                    onClick={() => setIsForgotPassword(false)}
                  >
                    Back to Login
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={classes.rightSide}>
          <Typography variant="h3">
            Welcome <br />
            to the <br /> Greenabl <br /> Platform!
          </Typography>
          <Typography style={{ marginTop: 32 }} variant="subtitle1">
            A digital platform combined with best-in-class research and
            consulting support to help shippers take a holistic approach to
            mitigating their carbon footprint.
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default Login;
